import React, { useState } from 'react';
import CalcShell from '../CalcShell';
import { Copy, RefreshCw } from 'lucide-react';

export default function PasswordCalc() {
  const [length, setLength] = useState(16);
  const [lower, setLower] = useState(true);
  const [upper, setUpper] = useState(true);
  const [num, setNum] = useState(true);
  const [sym, setSym] = useState(true);
  const [noAmbiguous, setNoAmbiguous] = useState(false);
  const [noRepeat, setNoRepeat] = useState(false);
  const [exclude, setExclude] = useState('');
  const [pw, setPw] = useState('');
  const [copied, setCopied] = useState(false);
  const [warn, setWarn] = useState('');

  const gen = () => {
    let chars = '';
    if (lower) chars += 'abcdefghijklmnopqrstuvwxyz';
    if (upper) chars += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    if (num) chars += '0123456789';
    if (sym) chars += '!@#$%^&*()_+-=[]{}<>?';
    if (noAmbiguous) chars = chars.split('').filter(c => !'Il1O0'.includes(c)).join('');
    if (exclude) chars = chars.split('').filter(c => !exclude.includes(c)).join('');
    // Dedupe pool for stable sampling
    chars = Array.from(new Set(chars.split(''))).join('');
    if (!chars) { setPw(''); setWarn('No characters available with current options.'); return; }
    if (noRepeat && length > chars.length) {
      setPw(''); setWarn(`No-repeat needs ≥ ${length} unique characters; pool has only ${chars.length}. Lower the length or enable more character sets.`); return;
    }
    setWarn('');
    let p = '';
    if (noRepeat) {
      // Fisher-Yates shuffle, take first N characters
      const arr = chars.split('');
      for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
      }
      p = arr.slice(0, length).join('');
    } else {
      for (let i = 0; i < length; i++) p += chars[Math.floor(Math.random() * chars.length)];
    }
    setPw(p);
  };

  const strength = pw ? Math.min(100, (pw.length * 6) + (lower ? 10 : 0) + (upper ? 10 : 0) + (num ? 10 : 0) + (sym ? 15 : 0)) : 0;
  const sColor = strength < 40 ? '#ef4444' : strength < 70 ? '#f59e0b' : '#10b981';

  return (
    <CalcShell look="cnet" title="Password Generator" subtitle="Create strong, random passwords with configurable options.">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Length: {length}</label><input type="range" min="4" max="64" value={length} onChange={e => setLength(+e.target.value)} /></div>
          <label className="flex items-center gap-2 py-1"><input type="checkbox" checked={lower} onChange={e => setLower(e.target.checked)} /> Lowercase (a-z)</label>
          <label className="flex items-center gap-2 py-1"><input type="checkbox" checked={upper} onChange={e => setUpper(e.target.checked)} /> Uppercase (A-Z)</label>
          <label className="flex items-center gap-2 py-1"><input type="checkbox" checked={num} onChange={e => setNum(e.target.checked)} /> Numbers (0-9)</label>
          <label className="flex items-center gap-2 py-1"><input type="checkbox" checked={sym} onChange={e => setSym(e.target.checked)} /> Symbols (!@#$)</label>
          <label className="flex items-center gap-2 py-1"><input type="checkbox" checked={noAmbiguous} onChange={e => setNoAmbiguous(e.target.checked)} /> Exclude ambiguous (I l 1 O 0)</label>
          <label className="flex items-center gap-2 py-1"><input type="checkbox" data-testid="pw-no-repeat" checked={noRepeat} onChange={e => setNoRepeat(e.target.checked)} /> No repeated characters</label>
          <div className="field mt-2"><label className="text-xs">Exclude specific characters</label>
            <input value={exclude} onChange={e => setExclude(e.target.value)} placeholder="e.g. abc123" className="font-mono" data-testid="pw-exclude"/>
          </div>
          <button onClick={gen} className="btn-primary mt-4 flex items-center gap-2"><RefreshCw className="w-4 h-4" /> Generate</button>
          {warn && <div className="text-xs text-red-600 mt-2" data-testid="pw-warn">{warn}</div>}
        </div>
        <div>
          <div className="result-panel">
            <div className="result-title">Password</div>
            <div className="flex items-center gap-2 mt-1">
              <div className="flex-1 font-mono text-lg font-semibold text-slate-900 break-all bg-white px-3 py-3 rounded-lg border border-slate-200">{pw || 'Click Generate'}</div>
              {pw && <button onClick={() => { navigator.clipboard.writeText(pw); setCopied(true); setTimeout(() => setCopied(false), 1500); }} className="p-3 rounded-lg bg-white border border-slate-200 hover:bg-slate-50"><Copy className="w-4 h-4" /></button>}
            </div>
            {copied && <div className="text-xs text-emerald-600 mt-2">Copied!</div>}
            {pw && <div className="mt-4">
              <div className="text-xs text-slate-600 mb-1">Strength</div>
              <div className="h-2 bg-slate-200 rounded-full overflow-hidden"><div className="h-full transition-all" style={{ width: strength + '%', background: sColor }}></div></div>
            </div>}
          </div>
        </div>
      </div>
    </CalcShell>
  );
}
